import { useState, useCallback, useEffect } from 'react';
import type { SectionId, SectionConfig, SubPanelMap, SlideDirection } from '../components/mobile/Navigation/Navigation.types';

interface UseSwipeNavigationConfig {
  sections: SectionConfig[];
  /** Which section index to start on (default: 2 = feed) */
  initialSection?: number;
}

interface UseSwipeNavigationReturn {
  /** Current main section index */
  currentSection: number;
  /** Direction of the last section transition (1 = right, -1 = left) */
  slideDirection: SlideDirection;
  /** Active sub-panel index per section (independent state) */
  subPanelMap: SubPanelMap;
  /** The current section's config object */
  activeSectionConfig: SectionConfig;
  /** The current sub-panel index for the active section */
  activeSubPanel: number;
  /** Switch to a section by index (bottom nav tap). Triggers slide animation. */
  goToSection: (index: number) => void;
  /** Change the sub-panel within a specific section */
  setSubPanel: (sectionId: SectionId, subPanelIndex: number) => void;
  /** Window width in px, tracked reactively for swipe snap calculations */
  screenWidth: number;
  /** Number of sections */
  sectionCount: number;
}

/**
 * Core navigation state hook.
 *
 * Responsibilities:
 * - Tracks which of the 5 sections is active
 * - Tracks which sub-panel (0–2) is active per section, independently
 * - Provides slide direction for section-transition animation
 * - Tracks screen width for sub-panel snap calculations
 * - Keyboard arrow keys move sub-panels within the active section
 */
export function useSwipeNavigation({
  sections,
  initialSection = 2,
}: UseSwipeNavigationConfig): UseSwipeNavigationReturn {
  const sectionCount = sections.length;

  const [currentSection, setCurrentSection] = useState(initialSection);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>(1);

  const [subPanelMap, setSubPanelMap] = useState<SubPanelMap>(() => {
    const map: Record<string, number> = {};
    for (const s of sections) map[s.id] = s.defaultSubPanel;
    return map as SubPanelMap;
  });

  const [screenWidth, setScreenWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 375,
  );

  // Resize tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let t: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => setScreenWidth(window.innerWidth), 100);
    };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(t); window.removeEventListener('resize', onResize); };
  }, []);

  // Arrow keys → move sub-panel within current section
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const sectionId = sections[currentSection].id;
      const current = subPanelMap[sectionId];

      if (e.key === 'ArrowLeft' && current > 0) {
        e.preventDefault();
        setSubPanelMap(prev => ({ ...prev, [sectionId]: current - 1 }));
        haptic('light');
      } else if (e.key === 'ArrowRight' && current < 2) {
        e.preventDefault();
        setSubPanelMap(prev => ({ ...prev, [sectionId]: current + 1 }));
        haptic('light');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentSection, subPanelMap, sections]);

  const goToSection = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, sectionCount - 1));
    setSlideDirection(clamped >= currentSection ? 1 : -1);
    setCurrentSection(clamped);
    haptic('medium');
  }, [currentSection, sectionCount]);

  const setSubPanel = useCallback((sectionId: SectionId, subPanelIndex: number) => {
    const clamped = Math.max(0, Math.min(subPanelIndex, 2));
    setSubPanelMap(prev => ({ ...prev, [sectionId]: clamped }));
    haptic('light');
  }, []);

  const activeSectionConfig = sections[currentSection];
  const activeSubPanel = subPanelMap[activeSectionConfig.id];

  return {
    currentSection,
    slideDirection,
    subPanelMap,
    activeSectionConfig,
    activeSubPanel,
    goToSection,
    setSubPanel,
    screenWidth,
    sectionCount,
  };
}

function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(style === 'light' ? 5 : style === 'medium' ? 15 : 30);
  }
}
